// src/lib/b2b.ts
// Línea B2B de velas industriales: se factura en COP directo (no wallet).
import { supabase } from '@/lib/supabase';
import { B2B } from '@/config/pricing';
import { currentBusinessId } from '@/lib/wallet';

export type B2BPlan = 'VELAS_B2B_MONTHLY' | 'VELAS_B2B_ANNUAL';

export const B2B_PLANS: Record<B2BPlan, { label: string; cop: number; days: number; detail: string }> = {
  VELAS_B2B_MONTHLY: {
    label: 'Velas B2B mensual',
    cop: B2B.VELAS_MONTHLY_COP,
    days: 30,
    detail: 'Datos industriales completos por 30 días.',
  },
  VELAS_B2B_ANNUAL: {
    label: 'Velas B2B anual',
    cop: B2B.VELAS_ANNUAL_COP,
    days: 365,
    detail: '12 meses al precio de 10 (2 meses gratis).',
  },
};

export interface B2BSubscription {
  id: string;
  business_id: string;
  plan: B2BPlan;
  cop_amount: number;
  expires_at: string;
  status: 'ACTIVE' | 'EXPIRED' | 'CANCELLED';
}

/** Suscripción B2B activa del negocio (si existe). */
export const getActiveB2B = async (): Promise<B2BSubscription | null> => {
  if (!supabase) return null;
  const businessId = currentBusinessId();
  if (!businessId) return null;
  const { data, error } = await supabase
    .from('b2b_subscriptions')
    .select('*')
    .eq('business_id', businessId)
    .eq('status', 'ACTIVE')
    .gt('expires_at', new Date().toISOString())
    .order('expires_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return data as unknown as B2BSubscription;
};

export interface B2BCheckoutResult {
  ok: boolean;
  url?: string;
  error?: string;
  fallbackWhatsApp?: string;
}

/**
 * Inicia la compra B2B. Si existe la Edge Function `wompi-create-payment`,
 * devuelve el link real de Wompi. Si no, ofrece el canal de WhatsApp con el
 * detalle para que NIIZ emita el link manualmente.
 */
export const startB2BCheckout = async (plan: B2BPlan): Promise<B2BCheckoutResult> => {
  const businessId = currentBusinessId();
  if (!businessId) return { ok: false, error: 'Inicia sesión para contratar el plan B2B.' };
  const selected = B2B_PLANS[plan];
  const whatsapp = `https://wa.me/573009701928?text=${encodeURIComponent(
    `Hola NIIZ, quiero contratar ${selected.label} por $${selected.cop.toLocaleString('es-CO')} COP. Negocio: ${businessId}.`,
  )}`;

  if (!supabase) return { ok: false, fallbackWhatsApp: whatsapp };

  try {
    const { data, error } = await (supabase as any).functions.invoke('wompi-create-payment', {
      body: { kind: 'b2b', plan, businessId },
    });
    if (error || !data?.url) {
      return { ok: false, fallbackWhatsApp: whatsapp, error: error?.message };
    }
    return { ok: true, url: String(data.url) };
  } catch (error) {
    return {
      ok: false,
      fallbackWhatsApp: whatsapp,
      error: error instanceof Error ? error.message : 'No se pudo crear el pago',
    };
  }
};

/** Historial B2B del negocio. */
export const listB2BSubscriptions = async (): Promise<B2BSubscription[]> => {
  if (!supabase) return [];
  const businessId = currentBusinessId();
  if (!businessId) return [];
  const { data } = await supabase
    .from('b2b_subscriptions')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });
  return (data as unknown as B2BSubscription[]) ?? [];
};
